import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Text,
    Center,
    Loader,
    Stack,
    Flex,
    TextInput,
    Button,
    Title,
    Pagination,
    Paper,
    Group,
    Badge,
} from "@mantine/core";
import { IconSearch, IconSortAscending, IconSortDescending } from "@tabler/icons-react";
import { Shift } from "../../types/Shift";
import HeroSectionV2 from "../../components/HeroSectionV2";
import Billede1 from "../../Images/forum.png";

interface Props {
    eventId: number;
    language: string;
}

const AllAvailableShiftListPublic: React.FC<Props> = ({ eventId, language }) => {
    const [shifts, setShifts] = useState<Shift[]>([]);
    const [loading, setLoading] = useState(true);
    const [showOnlyAvailable, setShowOnlyAvailable] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [sortAsc, setSortAsc] = useState(true);
    const [activePage, setActivePage] = useState(1);

    const navigate = useNavigate();
    const ITEMS_PER_PAGE = 25;

    useEffect(() => {
        const fetchShifts = async () => {
            try {
                const apiUrl = process.env.REACT_APP_API_URL;
                const shiftsRes = await fetch(`${apiUrl}/api/events/${eventId}/shifts/available`);
                const shiftsData = await shiftsRes.json();
                setShifts(shiftsData);
            } catch (error) {
                console.error("Failed to fetch shifts:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchShifts();
    }, [eventId]);

    useEffect(() => {
        setActivePage(1);
    }, [searchTerm, sortAsc, showOnlyAvailable]);

    if (loading) {
        return (
            <Center mt="md">
                <Loader color="red" />
            </Center>
        );
    }

    const filteredShifts = shifts
        .filter((s) => {
            const term = searchTerm.trim().toLowerCase();

            const startDate = new Date(s.startTime);

            const nameStr = (s.shiftsName ?? "").toLowerCase();
            const functionStr = (s.functionName ?? "").toLowerCase();

            const dayStr = startDate
                .toLocaleDateString("da-DK", { weekday: "long" })
                .toLowerCase();

            const dateStr = startDate.toLocaleDateString("da-DK"); // fx 4.5.2026
            const dateStrPadded = startDate.toLocaleDateString("da-DK", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
            }); // fx 04.05.2026

            const dayOnly = startDate.toLocaleDateString("da-DK", {
                day: "2-digit",
            }); // fx 04

            const monthOnly = startDate.toLocaleDateString("da-DK", {
                month: "2-digit",
            }); // fx 05

            const normalizedTerm = term.replace(/\s/g, "").replace(/-/g, ".").replace(/\//g, ".");
            const normalizedDate = dateStr.replace(/\s/g, "");
            const normalizedDatePadded = dateStrPadded.replace(/\s/g, "");

            const matchesSearch =
                term === "" ||
                nameStr.includes(term) ||
                functionStr.includes(term) ||
                dayStr.includes(term) ||
                normalizedDate.includes(normalizedTerm) ||
                normalizedDatePadded.includes(normalizedTerm) ||
                dayOnly.includes(normalizedTerm) ||
                monthOnly.includes(normalizedTerm);

            const matchesAvailability = !showOnlyAvailable || (s.availableSpots ?? 0) > 0;

            return matchesAvailability && matchesSearch;
        })
        .sort((a, b) => {
            const dateA = new Date(a.startTime).getTime();
            const dateB = new Date(b.startTime).getTime();
            return sortAsc ? dateA - dateB : dateB - dateA;
        });

    const totalPages = Math.ceil(filteredShifts.length / ITEMS_PER_PAGE);

    const paginatedShifts = filteredShifts.slice(
        (activePage - 1) * ITEMS_PER_PAGE,
        activePage * ITEMS_PER_PAGE
    );

    const handleGoToLogin = (shiftId: number) => {
        navigate(`/login?eventId=${eventId}&shiftId=${shiftId}`);
    };

    return (
        <div style={{ width: "100%", margin: "0 auto" }}>
                    <HeroSectionV2
                titleEn="Shifts"
                titleDa="Vagter"
                descriptionEn={"All available shifts"}
                descriptionDa={"Alle ledige vagter"}
                imageSrc={Billede1}
                badgeText=""
                logoSrc={""}
            />
        <div style={{ width: "60%", margin: "0 auto" }}>

            <Stack>
                <Center my="md" style={{ flexDirection: "column", gap: 8 }}>
                    <Title order={2}>
                        {language === "da" ? "Alle ledige vagter" : ""}
                    </Title>

                    <Text size="sm" c="dimmed">
                        {language === "da"
                            ? "Du kan se vagterne her, men skal logge ind for at vælge en vagt."
                            : "You can view shifts here, but you need to log in to choose a shift."}
                    </Text>

                    <Button
                        variant="light"
                        size="xs"
                        color="gray"
                        onClick={() => navigate(`/`)}
                    >
                        {language === "da" ? "← Tilbage til funktioner" : "← Back to functions"}
                    </Button>
                </Center>

                <Flex gap="sm" justify="center" align="flex-end" wrap="wrap" mt="md">
                    <TextInput
                        style={{ flex: 1, minWidth: "240px", maxWidth: "400px" }}
                        label={language === "da" ? "Søg vagt eller dato" : "Search shift or date"}
                        placeholder={language === "da" ? "f.eks. Fredag eller 17.04" : "e.g. Friday or 17.04"}
                        value={searchTerm}
                        onChange={(event) => setSearchTerm(event.currentTarget.value)}
                        leftSection={<IconSearch size={16} />}
                    />

                    <Button
                        variant="light"
                        onClick={() => setSortAsc(!sortAsc)}
                        leftSection={sortAsc ? <IconSortAscending size={16} /> : <IconSortDescending size={16} />}
                        style={{ height: "40px" }}
                    >
                        {language === "da" ? "Sorter dato" : "Sort date"}
                    </Button>
                </Flex>

                {filteredShifts.length === 0 ? (
                    <Text>{language === "da" ? "Ingen vagter fundet." : "No shifts found."}</Text>
                ) : (
                    <>
                        <Title order={4}>{language === "da" ? "Vagter" : "Shifts"}</Title>

                        <Text size="sm" c="dimmed">
                            {language === "da"
                                ? `Viser ${(activePage - 1) * ITEMS_PER_PAGE + 1}-${Math.min(
                                    activePage * ITEMS_PER_PAGE,
                                    filteredShifts.length
                                )} af ${filteredShifts.length} vagter`
                                : `Showing ${(activePage - 1) * ITEMS_PER_PAGE + 1}-${Math.min(
                                    activePage * ITEMS_PER_PAGE,
                                    filteredShifts.length
                                )} of ${filteredShifts.length} shifts`}
                        </Text>

                        <Stack gap="sm">
                            {paginatedShifts.map((shift) => {
                                const start = new Date(shift.startTime);
                                const end = new Date(shift.endTime);

                                return (
                                    <Paper key={shift.shiftsID} withBorder p="md" radius="md">
                                        <Group justify="space-between" align="flex-start" wrap="wrap">
                                            <Stack gap={4} style={{ flex: 1 }}>
                                                <Text fw={600}>{shift.shiftsName}</Text>

                                                {shift.functionName && (
                                                    <Text size="sm" c="dimmed">
                                                        {shift.functionName}
                                                    </Text>
                                                )}

                                                <Text size="sm">
                                                    {start.toLocaleDateString("da-DK", {
                                                        weekday: "long",
                                                        day: "2-digit",
                                                        month: "2-digit",
                                                        year: "numeric",
                                                    })}
                                                </Text>

                                                <Text size="sm">
                                                    {start.toLocaleTimeString("da-DK", {
                                                        hour: "2-digit",
                                                        minute: "2-digit",
                                                    })}
                                                    {" - "}
                                                    {end.toLocaleTimeString("da-DK", {
                                                        hour: "2-digit",
                                                        minute: "2-digit",
                                                    })}
                                                </Text>

                                                <Badge variant="light">
                                                    {language === "da"
                                                        ? `${shift.availableSpots ?? 0} ledige pladser`
                                                        : `${shift.availableSpots ?? 0} available spots`}
                                                </Badge>
                                            </Stack>

                                            <Button onClick={() => handleGoToLogin(shift.shiftsID)}>
                                                {language === "da" ? "Login for at vælge" : "Login to choose"}
                                            </Button>
                                        </Group>
                                    </Paper>
                                );
                            })}
                        </Stack>

                        {totalPages > 1 && (
                            <Center mt="md">
                                <Pagination
                                    value={activePage}
                                    onChange={setActivePage}
                                    total={totalPages}
                                />
                            </Center>
                        )}
                    </>
                )}
            </Stack>
            </div>
        </div>
    );
};

export default AllAvailableShiftListPublic;